import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, firstValueFrom, of, throwError } from 'rxjs';
import { mayAccessProjectViaCompanyScope } from 'shared_models';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../services/logger.service';
import { ProjectService } from '../services/project.service';
import { getProjectIdFromRoute } from './route-param.util';

async function userHasDirectProjectAssignment(
  projectService: ProjectService,
  userID: string,
  projectID: string
): Promise<boolean> {
  const assignmentResponse = await firstValueFrom(
    projectService.getUserProjectAssignment(userID, projectID, false).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(null);
        }
        return throwError(() => error);
      })
    )
  );
  return !!assignmentResponse?.data;
}

async function userMayLoadProjectViaCompanyScope(
  projectService: ProjectService,
  projectID: string
): Promise<boolean> {
  try {
    const response = await firstValueFrom(projectService.getProject(projectID, false));
    return !!response?.data;
  } catch (error) {
    const httpError = error as HttpErrorResponse;
    if (httpError?.status === 403 || httpError?.status === 404) {
      return false;
    }
    throw error;
  }
}

/**
 * Ensures the signed-in user may open project-scoped routes (`/project/:projectID/...`).
 * Field roles require a direct assignment; company-scoped admin roles may also use company access.
 */
export const projectAccessGuard: CanActivateFn = async route => {
  const authService = inject(AuthService);
  const projectService = inject(ProjectService);
  const router = inject(Router);
  const logger = inject(LoggerService);
  const projectID = getProjectIdFromRoute(route);

  let user;
  try {
    user = await firstValueFrom(authService.getCurrentUser());
  } catch {
    return router.parseUrl('/login');
  }

  if (!user?.userID) {
    return router.parseUrl('/login');
  }

  if (!projectID) {
    return router.parseUrl('/landing');
  }

  try {
    if (await userHasDirectProjectAssignment(projectService, user.userID, projectID)) {
      return true;
    }

    if (!mayAccessProjectViaCompanyScope(user.role)) {
      logger.warn('[ProjectAccessGuard] No assignment for field role:', user.role, projectID);
      return router.parseUrl('/landing');
    }

    if (await userMayLoadProjectViaCompanyScope(projectService, projectID)) {
      return true;
    }

    logger.warn('[ProjectAccessGuard] Access denied for user', user.userID, 'project', projectID);
    return router.parseUrl('/landing');
  } catch (error) {
    logger.error('[ProjectAccessGuard] Unexpected error verifying project access:', error);
    return router.parseUrl('/landing');
  }
};
